import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Mail, MessageSquare, X, Send, Calendar, Clock, Users, Megaphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_OPTIONS = [
  { value: 'all-upcoming-events', label: 'All Upcoming Event Registrants', desc: 'Everyone signed up for any upcoming Q&A or event' },
  { value: 'subscribers',          label: 'Subscribers',                     desc: 'Everyone who opted in via the Subscribe widget' },
  { value: 'leads',                label: 'All Leads',                       desc: 'Everyone in your leads database' },
  { value: 'resource-downloaders', label: 'Resource Downloaders',            desc: 'Everyone who grabbed a free resource (checklists, etc.)' },
  { value: 'resource',             label: 'One Specific Resource',           desc: 'Downloaders of a single resource (e.g. Jumpstart Checklist)' },
  { value: 'event',                label: 'One Specific Event',              desc: 'Registrants of a single event' },
  { value: 'all',                  label: 'Everyone',                        desc: 'Union of subscribers + leads + downloaders + event registrants, deduped by email' },
];

export default function ComposeBlastModal({ onClose, initialAudience = null, initialAudienceRef = null }) {
  const [audienceKind, setAudienceKind] = useState(initialAudience || 'all-upcoming-events');
  const [audienceRef, setAudienceRef] = useState(initialAudienceRef || '');
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [preview, setPreview] = useState({ total: 0, with_phone: 0, sample: [] });

  const [channels, setChannels] = useState(['email']);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);

  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'later'
  const [scheduleLocal, setScheduleLocal] = useState('');

  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/blast-templates`, { withCredentials: true })
      .then((res) => setTemplates(res.data.items || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/resources`)
      .then((res) => setResources(res.data || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/admin/events/list`, { withCredentials: true })
      .then((res) => setEvents(res.data.events || []))
      .catch(() => {});
  }, []);

  // Refresh preview whenever audience changes
  useEffect(() => {
    const params = { kind: audienceKind };
    if (audienceRef && (audienceKind === 'resource' || audienceKind === 'event')) {
      params.ref = audienceRef;
    } else if ((audienceKind === 'resource' || audienceKind === 'event') && !audienceRef) {
      setPreview({ total: 0, with_phone: 0, sample: [] });
      return;
    }
    axios
      .get(`${API_URL}/api/admin/blasts/audience-preview`, { params, withCredentials: true })
      .then((res) => setPreview(res.data))
      .catch(() => setPreview({ total: 0, with_phone: 0, sample: [] }));
  }, [audienceKind, audienceRef]);

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const tpl = templates.find((t) => t.template_id === tplId);
    if (!tpl) return;
    setSubject(tpl.subject || '');
    setMessage(tpl.body || '');
    toast.info(`Loaded template: ${tpl.name}`);
  };

  const toggleChannel = (c) => {
    setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c]);
  };

  const send = async () => {
    if (channels.length === 0) return toast.error('Pick at least one channel');
    if (!message.trim()) return toast.error('Message is required');
    if ((audienceKind === 'resource' || audienceKind === 'event') && !audienceRef) {
      return toast.error('Pick the specific event or resource first');
    }

    let scheduled_at = null;
    if (scheduleMode === 'later') {
      if (!scheduleLocal) return toast.error('Pick a send date/time');
      // Convert local datetime-local string to UTC ISO
      const localDate = new Date(scheduleLocal);
      if (isNaN(localDate.getTime())) return toast.error('Invalid date');
      if (localDate.getTime() <= Date.now()) return toast.error('Scheduled time must be in the future');
      scheduled_at = localDate.toISOString();
    }

    const verb = scheduleMode === 'later' ? `Schedule this blast for ${new Date(scheduleLocal).toLocaleString()}?` : `Send this blast to ${preview.total} people now?`;
    if (!window.confirm(verb)) return;

    setSending(true);
    try {
      const payload = {
        audience_kind: audienceKind,
        audience_ref: audienceRef || null,
        channels,
        subject: subject.trim() || undefined,
        message,
        scheduled_at,
      };
      const res = await axios.post(`${API_URL}/api/admin/blasts`, payload, { withCredentials: true });
      if (res.data.status === 'sent') {
        const s = res.data.stats || {};
        toast.success(`Blast sent now: ${s.email || 0} email, ${s.sms || 0} SMS, ${s.skipped || 0} skipped`);
      } else {
        toast.success(`Blast scheduled for ${new Date(res.data.scheduled_at).toLocaleString()}`);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create blast');
    } finally {
      setSending(false);
    }
  };

  const needsRef = audienceKind === 'resource' || audienceKind === 'event';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="compose-blast-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0A0F1C] flex items-center gap-2">
            <Megaphone size={18} className="text-[#F97316]" />
            Compose Blast
          </h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569]" data-testid="compose-close">
            <X size={20} />
          </button>
        </div>

        {/* AUDIENCE */}
        <div className="mb-4">
          <label className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-2 block">Audience</label>
          <select
            value={audienceKind}
            onChange={(e) => { setAudienceKind(e.target.value); setAudienceRef(''); }}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white"
            data-testid="audience-kind"
          >
            {AUDIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="text-xs text-[#94A3B8] mt-1">
            {AUDIENCE_OPTIONS.find((o) => o.value === audienceKind)?.desc}
          </p>
          {needsRef && (
            <select
              value={audienceRef}
              onChange={(e) => setAudienceRef(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white mt-2"
              data-testid="audience-ref"
            >
              <option value="">Pick {audienceKind === 'resource' ? 'a resource' : 'an event'}...</option>
              {audienceKind === 'resource'
                ? resources.map((r) => (
                    <option key={r.resource_id} value={r.resource_id}>{r.title}</option>
                  ))
                : events.map((e) => (
                    <option key={e.event_id} value={e.event_id}>
                      {e.event_date} - {e.title}
                    </option>
                  ))}
            </select>
          )}
          <div className="mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2 flex items-center gap-3 text-xs text-[#475569]" data-testid="audience-preview">
            <Users size={14} className="text-[#1D4ED8]" />
            <span><strong>{preview.total}</strong> recipients</span>
            <span className="text-[#94A3B8]">|</span>
            <span><strong>{preview.with_phone}</strong> have phone</span>
          </div>
        </div>

        {/* TEMPLATE */}
        {templates.length > 0 && (
          <div className="mb-3">
            <label className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-2 block">Template (optional)</label>
            <select
              onChange={(e) => applyTemplate(e.target.value)}
              defaultValue=""
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white"
              data-testid="compose-template-picker"
            >
              <option value="">Start from scratch</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}{t.tag ? ` (${t.tag})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* CHANNELS */}
        <div className="mb-3">
          <label className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-2 block">Channels</label>
          <div className="flex gap-4">
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={channels.includes('email')} onChange={() => toggleChannel('email')} data-testid="compose-email" />
              <Mail size={14} /> Email
            </label>
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={channels.includes('sms')} onChange={() => toggleChannel('sms')} data-testid="compose-sms" />
              <MessageSquare size={14} /> SMS
            </label>
          </div>
        </div>

        {/* SUBJECT */}
        {channels.includes('email') && (
          <Input
            placeholder="Email subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mb-3"
            data-testid="compose-subject"
          />
        )}

        {/* MESSAGE */}
        <textarea
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-3"
          data-testid="compose-message"
        />

        {/* SCHEDULE */}
        <div className="mb-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          <label className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-2 block">When to send</label>
          <div className="flex gap-3 mb-2">
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input type="radio" name="schedmode" checked={scheduleMode === 'now'} onChange={() => setScheduleMode('now')} data-testid="schedule-now" />
              <Send size={14} /> Send Now
            </label>
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input type="radio" name="schedmode" checked={scheduleMode === 'later'} onChange={() => setScheduleMode('later')} data-testid="schedule-later" />
              <Calendar size={14} /> Schedule for Later
            </label>
          </div>
          {scheduleMode === 'later' && (
            <input
              type="datetime-local"
              value={scheduleLocal}
              onChange={(e) => setScheduleLocal(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white"
              data-testid="schedule-datetime"
            />
          )}
        </div>

        <Button
          onClick={send}
          disabled={sending}
          className="btn-primary-orange w-full flex items-center justify-center gap-2"
          data-testid="compose-submit"
        >
          {scheduleMode === 'later' ? <Calendar size={14} /> : <Send size={14} />}
          {sending ? 'Working...' : (scheduleMode === 'later' ? `Schedule Blast` : `Send Now to ${preview.total}`)}
        </Button>
      </div>
    </div>
  );
}
